import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import { authContext, Profile } from '@staffedupusa/shared-components';
import {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useGetApplicantDetails } from '@features/applicant/hooks/useGetApplicantDetails';
import ApplicationNotes from '@features/applicationNotes/components/ApplicationNotes/ApplicationNotes';
import { Application } from '@features/applications';
import ApplicantViewApplicantInfo from '@features/applications/components/ApplicantViewApplicantInfo/ApplicantViewApplicantInfo';
import ApplicationViewActions from '@features/applications/components/ApplicationViewActions/ApplicationViewActions';
import { sortApplicationsByDate } from '@features/applications/components/ApplicationViewModal/utils';
import ApplicationViewModalHeader from '@features/applications/components/ApplicationViewModalHeader/ApplicationViewModalHeader';
import ApplicationViewTabs from '@features/applications/components/ApplicationViewTabs/ApplicationViewTabs';
import { useGetApplicantApplications } from '@features/applications/hooks/useGetApplicantApplications';
import { lightGrey } from '@styles/colors';
import { useGetEnterpriseApplicantApplications } from '@features/applications/hooks/useGetEnterpriseApplicantApplications';
import EnterpriseApplicationViewModalHeader from '../EnterpriseApplicationViewModalHeader/EnterpriseApplicationViewModalHeader';
import EnterpriseApplicationNotes from '@features/applicationNotes/components/EnterpriseApplicationNotes/EnterpriseApplicationNotes';
import CloseIcon from '@mui/icons-material/Close';

type EnterpriseApplicationViewModalProps = {
  handleClose: () => void;
  application: Application | null;
  searchValue: string;
  setApplication: Dispatch<SetStateAction<Application | null>>;
  selectedCompanyId?: string | number;
  setApplicationData?: Dispatch<SetStateAction<Application[] | null>>;
  handlePageNo?: (newPageNo: number) => void;
  updateRating?: (id: number, newRating: number) => void;
};

const EnterpriseApplicationViewModal: FC<
  EnterpriseApplicationViewModalProps
> = ({
  handleClose,
  application,
  searchValue,
  handlePageNo,
  setApplication,
  selectedCompanyId,
  setApplicationData,
  updateRating,
}) => {
    const { authData } = useContext(authContext);
    const [errorText, setErrorText] = useState<string>('');
    const [isApplicationNotesShown, setIsApplicationNotesShown] = useState(false);

    const { enterpriseApplicantApplications } =
      useGetEnterpriseApplicantApplications({
        setErrorText,
        id: application?.applicant?.id,
        isEnterprise: authData?.user?.enterprise ? true : false,
        company_id: application?.job_listing?.company?.id,
      });

    const { applicantDetailsData } = useGetApplicantDetails({
      applicantId: application?.applicant?.id,
    });

    if (!application) {
      return null;
    }

    const handleModalClose = () => {
      setIsApplicationNotesShown(false);
      handleClose();
    };

    return (
      <Dialog
        open
        onClose={handleModalClose}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle sx={{ bgcolor: lightGrey, pb: 5 }}>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleModalClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: lightGrey }}>
          <Stack spacing={3}>
            {errorText && <Alert severity="error">{errorText}</Alert>}
            <EnterpriseApplicationViewModalHeader
              application={application}
              setApplication={setApplication}
              enterpriseApplicantApplications={sortApplicationsByDate(
                (enterpriseApplicantApplications?.object as Application[]) || [],
              )}
              applicantDetailsData={
                applicantDetailsData?.object as Profile | null
              }
            />
            <ApplicantViewApplicantInfo
              application={application}
              applicantDetailsData={applicantDetailsData?.object || null}
              searchValue={searchValue}
              selectedCompanyId={selectedCompanyId}
              toggleApplicationNotesModal={() =>
                setIsApplicationNotesShown((prev) => !prev)
              }
              setApplicationData={setApplicationData}
              setApplication={setApplication}
              updateRating={updateRating}
            />
            {isApplicationNotesShown ? (
              <EnterpriseApplicationNotes
                applicationId={application.id}
                companyId={application.job_listing.company.id}
                handleClose={() => setIsApplicationNotesShown(false)}
              />
            ) : (
              <>
                <ApplicationViewActions
                  application={application}
                  setApplication={setApplication}
                  selectedCompanyId={selectedCompanyId}
                  setApplicationData={setApplicationData}
                  handlePageNo={handlePageNo}
                />
                <ApplicationViewTabs
                  application={application}
                  applicationDetails={applicantDetailsData?.object || null}
                />
              </>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    );
  };

export default EnterpriseApplicationViewModal;
